import React, { useMemo } from "react";
import { createTheme, ThemeProvider, useTheme } from "@material-ui/core/styles";
import { getCrmColors } from "../../theme/crmColors";

export function createCrmTheme(outerTheme) {
  const dark = outerTheme.palette.type === "dark";
  const colors = getCrmColors(outerTheme);
  const github = outerTheme.uiPalette === "github";
  return createTheme({
    ...outerTheme,
    crm: colors,
    palette: {
      ...outerTheme.palette,
      type: dark ? "dark" : "light",
      primary: {
        main: colors.accent,
        light: github ? colors.focus : dark ? "#A5C8FF" : "#5681D8",
        dark: github ? colors.accent : dark ? "#6A9CEB" : "#19469F",
        contrastText: github && dark ? colors.background : dark ? "#102039" : "#FFFFFF"
      },
      text: { primary: colors.text, secondary: colors.muted, disabled: github ? colors.emptyStars : dark ? "#7E8EA4" : "#8995A8", hint: colors.muted },
      background: { default: colors.background, paper: colors.surface },
      divider: colors.border,
      error: { main: dark ? "#FF9C9C" : "#BE3445" },
      warning: { main: colors.stars },
      success: { main: dark ? "#7ADCB5" : "#16774E" },
      info: { main: colors.accent },
      action: {
        ...outerTheme.palette.action,
        active: colors.muted,
        hover: colors.hover,
        selected: colors.selected,
        disabled: github ? colors.emptyStars : dark ? "#7E8EA4" : "#8995A8",
        disabledBackground: github ? colors.raised : dark ? "#253247" : "#E7ECF4",
        focus: colors.accentSoft
      }
    },
    overrides: {
      ...outerTheme.overrides,
      MuiPaper: { root: { color: colors.text, backgroundColor: colors.surface }, outlined: { borderColor: colors.border } },
      MuiTypography: { root: { color: "inherit" }, colorTextSecondary: { color: colors.muted } },
      MuiButton: {
        root: { textTransform: "none", fontWeight: 600, borderRadius: 8, "&.Mui-focusVisible": { outline: `2px solid ${colors.focus}`, outlineOffset: 2 } },
        contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        text: { "&:hover": { backgroundColor: colors.hover } }
      },
      MuiIconButton: {
        root: { color: colors.muted, "&:hover": { backgroundColor: colors.hover }, "&.Mui-focusVisible": { outline: `2px solid ${colors.focus}`, outlineOffset: 2 } }
      },
      MuiOutlinedInput: {
        root: { backgroundColor: colors.inset, borderRadius: 8, "&:hover $notchedOutline": { borderColor: colors.muted } },
        notchedOutline: { borderColor: colors.border },
        input: { colorScheme: dark ? "dark" : "light", "&::placeholder": { color: colors.muted, opacity: 1 } }
      },
      MuiInputLabel: { root: { color: colors.muted } },
      MuiFormLabel: { root: { color: colors.muted } },
      MuiFormHelperText: { root: { color: colors.muted } },
      MuiInputBase: { root: { color: colors.text } },
      MuiSelect: { icon: { color: colors.muted } },
      MuiListItem: { button: { "&:hover": { backgroundColor: colors.hover } } },
      MuiMenuItem: { root: { color: colors.text, "&:hover": { backgroundColor: colors.hover } } },
      MuiDialog: { paper: { border: `1px solid ${colors.border}`, borderRadius: 14, backgroundImage: "none" } },
      MuiDialogTitle: { root: { padding: "22px 24px 16px" } },
      MuiDialogContent: {
        root: {
          padding: "8px 24px 20px", colorScheme: dark ? "dark" : "light",
          scrollbarColor: `${colors.emptyStars} ${colors.surface}`, scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-track, &::-webkit-scrollbar-corner": { backgroundColor: colors.surface },
          "&::-webkit-scrollbar-thumb": { backgroundColor: colors.emptyStars, borderRadius: 8, border: `2px solid ${colors.surface}` },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: colors.muted }
        }
      },
      MuiDialogActions: { root: { padding: "12px 24px 20px" } },
      MuiChip: { root: { color: colors.text, backgroundColor: colors.raised }, outlined: { backgroundColor: "transparent", borderColor: colors.border } },
      MuiTab: { textColorPrimary: { color: colors.muted } },
      MuiDivider: { root: { backgroundColor: colors.border } },
      MuiTooltip: { tooltip: { color: colors.text, backgroundColor: colors.raised, border: `1px solid ${colors.border}` } },
      MuiRating: { iconFilled: { color: colors.stars }, iconEmpty: { color: colors.emptyStars }, iconHover: { color: colors.stars } }
    }
  });
}

export default function CrmTheme({ children }) {
  const outerTheme = useTheme();
  const theme = useMemo(() => createCrmTheme(outerTheme), [outerTheme]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
